import type { ColDef } from 'ag-grid-community'

/** Col id of the ⋯ column in both operations grids. */
export const ACTIONS_COL_ID = 'actions'

/**
 * True when a grid row click originated inside the ⋯ actions cell.
 *
 * Selecting a row swaps the detail panel and re-renders the grid, which tears a
 * just-opened dropdown straight back down — so a click on ⋯ must not also count
 * as a row selection. React's `stopPropagation` on the trigger is not enough:
 * AG Grid attaches its own row listener natively, outside React's synthetic
 * event system, so it still fires. Testing the DOM target against the cell's
 * `col-id` is the reliable guard.
 */
export const isActionsCellClick = (
  event: Event | null | undefined
): boolean => {
  const target = event?.target
  if (!(target instanceof Element)) {
    return false
  }
  return Boolean(target.closest(`[col-id="${ACTIONS_COL_ID}"]`))
}

/**
 * True when a click landed on AG Grid's row-drag grip.
 *
 * Matched on the grid's own class rather than a column id: the grip is an
 * affordance hosted inside whichever cell carries `rowDrag` (the seed cell
 * here), so there is no drag COLUMN to test for. A click on it must not also
 * select the row, which would swap the detail panel mid-gesture.
 */
const isRowDragHandleClick = (event: Event | null | undefined): boolean => {
  const target = event?.target
  if (!(target instanceof Element)) {
    return false
  }
  return Boolean(target.closest('.ag-drag-handle'))
}

/**
 * Whether a clicked cell should be left alone rather than selecting its row.
 *
 * Three cases: the ⋯ menu, the row-drag grip, and any cell the user can edit in
 * place. Selecting a row swaps the detail panel and re-renders the grid, so
 * without this an editable cell never reaches edit mode and the field looks
 * read-only.
 *
 * Takes the COLUMN DEFINITION, not the DOM. AG Grid has no
 * "this cell is editable" class — it only marks a cell once editing has already
 * STARTED (`ag-cell-inline-editing`, `ag-cell-popup-editing`), which is too
 * late to suppress the click that should have begun it. `colDef.editable` is
 * the same value the grid itself consults, and it arrives on the
 * `cellClicked` event.
 *
 * `editable` may be a predicate, in which case it is evaluated against the row
 * — a per-row rule (a locked email cell, say) must not suppress selection on
 * rows where it returns false.
 */
export const shouldIgnoreCellClick = <TRow>(
  colDef: ColDef<TRow> | null | undefined,
  data: TRow | undefined,
  event: Event | null | undefined
): boolean => {
  if (isActionsCellClick(event)) {
    return true
  }
  if (isRowDragHandleClick(event)) {
    return true
  }
  if (!colDef) {
    return false
  }
  const { editable } = colDef
  if (typeof editable === 'function') {
    // The predicate wants the full params object; only `data` and `colDef` are
    // consulted by the ones in this feature.
    return Boolean(editable({ data, colDef } as never))
  }
  return Boolean(editable)
}
